import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, cloneDeep, isObjectLike, isString, isNumber, clamp } from 'lodash';
import { Input, InputNumber, AutoComplete } from 'antd';
import { sanitizeFloatNumber } from '../util';
import FormItem from '../styled/FormItem';
import { Form as LegacyForm } from '@ant-design/compatible';

const Context = React.createContext();

// use this to have access to the row form outside of the component
export const EditableContext = Context.Consumer;
export const RowEditable = LegacyForm.create({ name: 'row_editable' })(({ form, record, className, ...props }) => (
  <Context.Provider value={{ form, record }}>
    <tr className={classNames(className, record.className)} {...props} />
  </Context.Provider>
));

const getInputOptions = options => {
  const opt = { type: 'text' };
  if (isObjectLike(options)) Object.assign(opt, cloneDeep(options));
  else if (typeof options === 'string') opt.type = options;
  // remove chars from number
  if (opt.type === 'number' && !opt.parser) {
    const min = get(opt, 'min', Number.NEGATIVE_INFINITY);
    const max = get(opt, 'max', Number.POSITIVE_INFINITY);
    opt.parser = val => {
      let no = sanitizeFloatNumber(val);
      if (min >= 0) {
        // transform negative numbers into positive
        if (isString(no)) no = no.replace('-', '');
        else if (isNumber(no)) no = clamp(Math.abs(no), min, max);
      }
      if (max <= 0 && isNumber(no) && no > 0) {
        // transform positive numbers into negative
        no = clamp(no * -1, min, max);
      }
      return no;
    };
  }
  return opt;
};

/**
 * Prepare options to be passed as input props
 * @param {Object} options
 * @param {Object} param adds this param at the end of all functions from options
 */
const prepareInputOptions = (options, param) => {
  const opts = getInputOptions(options);
  Object.keys(opts).reduce((o, key) => {
    if (typeof o[key] !== 'function') return o;
    const func = o[key];
    o[key] = (...args) => func(...args, param);
    return o;
  }, opts);
  if (typeof opts.disabled === 'function') opts.disabled = opts.disabled();
  return opts;
};

class CellEditable extends React.PureComponent {
  getInput = form => {
    const { columnKey, recordKey, options } = this.props;
    const param = { columnKey, recordKey, form };
    const opts = prepareInputOptions(typeof options === 'function' ? options(param) : options, param);
    const { type, ...props } = opts;
    switch (type) {
      case 'autocomplete': {
        props.options = props.dataSource;
        delete props.dataSource;
        return <AutoComplete className='cell-editable-autocomplete' {...props} />;
      }
      case 'number':
        return <InputNumber {...props} />;
      default:
        return <Input {...props} />;
    }
  };

  render() {
    const { columnKey, validator, children } = this.props;
    return (
      <EditableContext>
        {({ form }) => {
          const { getFieldDecorator } = form;
          const decorator = { initialValue: get(children, 'value', children) };
          if (typeof validator === 'function') decorator.rules = [{ validator: validator(form, columnKey) }];
          return (
            <FormItem style={{ margin: 0 }}>
              {getFieldDecorator(columnKey, decorator)(this.getInput(form))}
            </FormItem>
          );
        }}
      </EditableContext>
    );
  }
}

CellEditable.propTypes = {
  columnKey: PropTypes.string.isRequired,
  recordKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),
  validator: PropTypes.func,
  children: PropTypes.any,
};

CellEditable.defaultProps = {
  options: '',
  validator: () => {},
  children: null,
};

export default CellEditable;
